// services/session.service.js
import {
	collection,

	query,
	where,
	getDocs,
	updateDoc,
	doc,
	getDoc,
	Timestamp,
	orderBy,
	writeBatch,
	increment,
} from "firebase/firestore";
import { db } from "../config/firebase";

export const sessionService = {
	// Get all sessions for a user
	getUserSessions: async (userId) => {
		try {
			const sessionsRef = collection(db, "sessions");
			const q = query(
				sessionsRef,
				where("userId", "==", userId),
				orderBy("date", "desc")
			);

			const snapshot = await getDocs(q);
			return snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
				// Don't convert timestamps here - let Redux handle serialization
			}));
		} catch (error) {
			console.error("Error fetching user sessions:", error);
			throw error;
		}
	},
	// Get available time slots for a given date
	getAvailableSlots: async (date) => {
		console.log("Service getting slots for:", date);
		try {
			const startOfDay = new Date(date);
			startOfDay.setHours(0, 0, 0, 0);

			const endOfDay = new Date(date);
			endOfDay.setHours(23, 59, 59, 999);

			// Get all booked sessions for the date
			const sessionsQuery = query(
				collection(db, "sessions"),
				where("date", ">=", Timestamp.fromDate(startOfDay)),
				where("date", "<=", Timestamp.fromDate(endOfDay)),
				where("status", "in", ["pending", "confirmed"])
			);

			const snapshot = await getDocs(sessionsQuery);
			const bookedSlots = snapshot.docs.map((doc) => doc.data().timeSlot);

			// Available time slots (9 AM to 5 PM, 2-hour slots)
			const allSlots = ["09:00", "13:00", "17:00"];

			// Filter out past slots if the date is today
			const now = new Date();
			const isToday = startOfDay.toDateString() === now.toDateString();

			const availableSlots = allSlots.filter((slot) => {
				// Remove booked slots
				if (bookedSlots.includes(slot)) return false;

				// If it's today, remove past time slots
				if (isToday) {
					const [hours, minutes] = slot.split(":");
					const slotTime = new Date(now);
					slotTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
					return slotTime > now;
				}

				return true;
			});

			return availableSlots;
		} catch (error) {
			console.error("Error fetching available slots:", error);
			throw error;
		}
	},

	// Request a new session
	requestSession: async (userId, sessionData) => {
		const batch = writeBatch(db);
		let sessionRef;

		try {
			// Get user's subscription
			const subscriptionQuery = query(
				collection(db, "subscriptions"),
				where("userId", "==", userId),
				where("status", "==", "active")
			);
			const subscriptionSnapshot = await getDocs(subscriptionQuery);

			if (subscriptionSnapshot.empty) {
				throw new Error("No active subscription found");
			}

			const subscriptionDoc = subscriptionSnapshot.docs[0];
			const subscription = subscriptionDoc.data();

			// Verify credits again (double-check)
			if (subscription.availableCredits < sessionData.duration) {
				throw new Error("Insufficient credits");
			}

			// Create session document with batch
			sessionRef = doc(collection(db, "sessions"));
			batch.set(sessionRef, {
				userId,
				subscriptionId: subscriptionDoc.id,
				status: "pending",
				createdAt: Timestamp.now(),
				...sessionData,
				date: Timestamp.fromDate(sessionData.date),
			});

			// Deduct credits
			batch.update(doc(db, "subscriptions", subscriptionDoc.id), {
				availableCredits: increment(-sessionData.duration),
			});

			// Add credit transaction
			const transactionRef = doc(collection(db, "creditTransactions"));
			batch.set(transactionRef, {
				userId,
				sessionId: sessionRef.id,
				subscriptionId: subscriptionDoc.id,
				type: "deduction",
				amount: sessionData.duration,
				balanceAfter: subscription.availableCredits - sessionData.duration,
				createdAt: Timestamp.now(),
			});

			await batch.commit();
			return sessionRef.id;
		} catch (error) {
			console.error("Error requesting session:", error);
			throw error;
		}
	},

	// Cancel a session
	cancelSession: async (sessionId) => {
		const batch = writeBatch(db);

		try {
			const sessionRef = doc(db, "sessions", sessionId);
			const sessionDoc = await getDoc(sessionRef);

			if (!sessionDoc.exists()) {
				throw new Error("Session not found");
			}

			const session = sessionDoc.data();
			if (session.status === "completed") {
				throw new Error("Cannot cancel a completed session");
			}

			// Get subscription
			const subscriptionRef = doc(db, "subscriptions", session.subscriptionId);
			const subscriptionDoc = await getDoc(subscriptionRef);
			const subscription = subscriptionDoc.data();

			// Update session status
			batch.update(sessionRef, {
				status: "cancelled",
				cancelledAt: Timestamp.now(),
				updatedAt: Timestamp.now(),
			});

			// Refund credits
			batch.update(subscriptionRef, {
				availableCredits: increment(session.duration),
			});

			// Record credit refund
			const transactionRef = doc(collection(db, "creditTransactions"));
			batch.set(transactionRef, {
				userId: session.userId,
				sessionId,
				subscriptionId: session.subscriptionId,
				type: "refund",
				amount: session.duration,
				balanceAfter: subscription.availableCredits + session.duration,
				reason: "session_cancelled",
				createdAt: Timestamp.now(),
			});

			await batch.commit();
			return true;
		} catch (error) {
			console.error("Error cancelling session:", error);
			throw error;
		}
	},

	// Reschedule a session
	rescheduleSession: async (sessionId, newDate, newTimeSlot) => {
		try {
			const sessionRef = doc(db, "sessions", sessionId);
			const sessionDoc = await getDoc(sessionRef);

			if (!sessionDoc.exists()) {
				throw new Error("Session not found");
			}

			const session = sessionDoc.data();
			if (session.status === "completed") {
				throw new Error("Cannot reschedule a completed session");
			}

			// Check if new slot is available
			const availableSlots = await this.getAvailableSlots(newDate);
			if (!availableSlots.includes(newTimeSlot)) {
				throw new Error("Selected time slot is not available");
			}

			await updateDoc(sessionRef, {
				date: Timestamp.fromDate(new Date(newDate)),
				timeSlot: newTimeSlot,
				status: "rescheduled",
				rescheduledAt: Timestamp.now(),
				updatedAt: Timestamp.now(),
			});
		} catch (error) {
			console.error("Error rescheduling session:", error);
			throw error;
		}
	},

	// Complete a session
	completeSession: async (sessionId) => {
		try {
			const sessionRef = doc(db, "sessions", sessionId);
			await updateDoc(sessionRef, {
				status: "completed",
				completedAt: Timestamp.now(),
				updatedAt: Timestamp.now(),
			});
		} catch (error) {
			console.error("Error completing session:", error);
			throw error;
		}
	},
};
